

import React, { Component } from 'react';
import cx from 'classnames';
import _isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import _get from 'lodash.get';
import { getOutputSummaryWithAdditionalOutputs } from '@shootsta/addons-summarizer';
import { getCostValue } from '@shootsta/cost-parsing-utils';
import { PAYMENT_STATUS, VIDEO_OUTPUT_STATUS } from '@shootsta/common-domain-data';
import { credentials } from '@shootsta/client-auth';
import {
  isProjectSettled,
  hasDollarBillables,
  deriveFinanceCredits,
  repriceAdditionalOutputsFromFinance,
  getEffectiveIterationCost } from
'@core/shared/utils/financeLedger';
import {
  resolveDisplayedProdCredits,
  resolveDisplayedCreativeServiceCredits,
  formatCreditsLabel } from
'../../Credits/selectors/resolveDisplayedProdCredits';


import { FormModal, Text, Loader, Link } from '../../../ui-kit';
import addExtrasTotalCost from '../../../common/utils/addExtrasTotalCost';
import { withStockAssets } from '../../../common/hocs';
import { getOutputCreditAmount } from '../utils';
import { toBeautifulDecimal } from '../../../common/utils';
import PaymentSummaryContentV2 from './PaymentSummaryContent/PaymentSummaryContentV2';

import AmountDue from './PaymentSummaryContent/components/AmountDue';

import '../styles/_payment-summary.scss';
















































































function getOutputsCreditAmounts(videoOutputs, projectType) {
  return videoOutputs.reduce((creditAmounts, output) => ({
    ...creditAmounts,
    [output.outputId]: getOutputCreditAmount(output, projectType)
  }), {});
}

function getPurchaseOrderId(videoOutputs) {
  const masterOutput = videoOutputs.find((output) => Boolean(output.isMaster) === true) || {};
  const versions = masterOutput.versions || [];

  const versionWithPurchaseOrderId = versions.find((version) => !!version.purchaseOrderId);
  const purchaseOrderId = versionWithPurchaseOrderId ? versionWithPurchaseOrderId.purchaseOrderId : '';

  return purchaseOrderId || '';
}

function getVersionIdWithPurchaseOrderId(videoOutputs) {
  const masterOutput = videoOutputs.find((output) => Boolean(output.isMaster) === true) || {};
  const versions = masterOutput.versions || [];

  const versionWithPurchaseOrderId = versions.find((version) => !!version.purchaseOrderId);

  return versionWithPurchaseOrderId ? versionWithPurchaseOrderId.versionId : versions[0].versionId;
}

// Exporting this class to keep consistent behaviour in tests
export class PaymentSummaryComponent extends Component {
  constructor(props) {
    super(props);

    const outputCreditAmounts = getOutputsCreditAmounts(props.videoOutputs, props.projectType);
    const purchaseOrderId = getPurchaseOrderId(props.videoOutputs);

    this.state = {
      loading: true,
      availableCredits: 0,
      totalCost: '-1',
      outputCreditAmounts,
      initialOutputCreditAmounts: outputCreditAmounts,
      // Finance-derived production credits summed across all outputs. null until
      // the finance-ledger read resolves (and stays null when the flag is off),
      // in which case the displayed total falls back to the static per-output
      // sum. Once loaded, Finance is the source of truth so credit-funded
      // iterations are counted (matching the Costs tab).
      financeProdCredits: null,
      // Finance-derived creative-services credits (recorded against the master
      // output). null until the finance read resolves; the displayed total then
      // sums production + creative services from Finance, matching the Costs
      // tab, instead of the stale getCreditsConsumption value.
      financeCreativeServiceCredits: null,
      // Finance-derived credits per output, keyed by outputId:
      // { [outputId]: { prodCredits, creativeServiceCredits } }. null until the
      // finance read resolves; lets the V2 per-output dropdown show the real
      // production + creative-services breakdown instead of the stale legacy 0s.
      financePerOutputCredits: null,
      // Effective iteration ($) cost per output (getEffectiveIterationCost),
      // keyed by outputId: { [outputId]: iterationCost }. null until the finance
      // read resolves; used to reprice additional outputs in the cost summary so
      // a credit-funded / invoice-$0 iteration stops contributing its stale
      // `outputCost` snapshot. Billed → Finance $, unbilled → current region
      // default (when in scope); see fetchFinanceCredits.
      financePerOutputIterationCost: null,
      purchaseOrderId,
      creativeServicesCredits: 0,
      initialCreativeServicesCredits: 0,
      contractOutputCredits: undefined,
      initialContractOutputCredits: undefined,
      hasShutterStockAccess: false,
      totalProjectCost: '',
      // Credits-only portion of totalProjectCost (production + creative services,
      // no add-on dollars) — drives V1's "Total credit" line.
      totalProjectCredits: '',
      hasActiveContract: false,
      paymentStatus: '',
      totalAmountPending: 0,
      addonsSettled: false,
      settledWithDollars: false,
      usedDollars: false,
      settlementLoaded: false
    };
  }

  componentDidMount() {
    this.setTotalCost();
    this.fetchCreditsDetails();
    this.fetchFinanceCredits();
    this.calculateTotalProjectCost();
    this.loadPaymentStatus();
    this.loadAddonsSettlement();
  }

  componentDidUpdate(prevProps) {
    const {
      videoOutputs: prevOutputs
    } = prevProps;

    const {
      videoOutputs: newOutputs,
      organisationId,
      userSubmittedTeamId
    } = this.props;

    if (!isEqual(prevOutputs, newOutputs)) {
      this.setOutputCreditsState();
      this.setTotalCost();
      this.fetchFinanceCredits();
    }

    if (prevProps.organisationId !== organisationId ||
    prevProps.userSubmittedTeamId !== userSubmittedTeamId)
    {
      this.fetchCreditsDetails();
      this.fetchFinanceCredits();
    }

    this.calculateTotalProjectCost();
  }

  setOutputCreditsState = () => {
    const { videoOutputs, projectType } = this.props;

    const outputCreditAmounts = getOutputsCreditAmounts(videoOutputs, projectType);

    this.setState({
      initialOutputCreditAmounts: outputCreditAmounts,
      outputCreditAmounts
    });
  };

  getAdditionalOutputs = (videoOutputs, output) => {
    if (output.isMaster) {
      return videoOutputs.filter((o) => o.outputId !== output.outputId);
    }
    return [];
  };

  getOutputNameV2 = (output) => {
    const { videoName } = this.props;
    const { name, isMaster, aspectRatio } = output;
    const outputName = name || videoName;

    if (isMaster) {
      return `${outputName} (Master)`;
    }

    const safeAspectRatio = typeof aspectRatio === 'string' && aspectRatio.trim() ?
    aspectRatio.trim() :
    'Unknown';

    const outputAspect = safeAspectRatio.charAt(0).toUpperCase() +
    safeAspectRatio.slice(1).toLowerCase();

    return `${outputName} (${outputAspect})`;
  };

  getOutputsValues() {
    const { videoOutputs } = this.props;
    const { financePerOutputIterationCost } = this.state;

    return videoOutputs.map((output) => {
      const additionalOutputs = this.getAdditionalOutputs(videoOutputs, output);
      // The summarizer prices each additional output (Social Iteration) from its
      // stale `outputCost` snapshot. When Finance has resolved, reprice the ones
      // that have a Finance record so a credit-funded / invoice-$0 iteration
      // contributes its real Finance $ (0) instead of the stale snapshot,
      // matching the Costs tab. Outputs without a record — and the whole
      // off-flag / finance-unreachable path (map stays null) — keep the legacy
      // snapshot value.
      const pricedAdditionalOutputs = financePerOutputIterationCost
        ? repriceAdditionalOutputsFromFinance(
            additionalOutputs,
            financePerOutputIterationCost
          )
        : additionalOutputs;
      const {
        totalCost,
        addonsList
      } = getOutputSummaryWithAdditionalOutputs(output, pricedAdditionalOutputs);
      const newAddons = addonsList.
      map(({ __typename, ...addon }) => addon).
      filter((item) => Object.keys(item).length > 0);

      return {
        outputName: this.getOutputNameV2(output),
        totalCost,
        output,
        // Return the repriced list, not the raw one — the expandable breakdown
        // (OutputAddonsList) re-summarizes this array to draw the per-output
        // line items, so it must use the same Finance-repriced costs the
        // totalCost above was computed from, or a credit-funded iteration's
        // line item would show the stale snapshot under a repriced total.
        additionalOutputs: pricedAdditionalOutputs,
        numberOfAddons: newAddons.length || 0
      };
    });
  }

  getFormattedTotalCost(totalCost, isEstimatedTotal) {
    const { getCurrencySymbol, appliedStockAssets, loadingStockAssets } = this.props;
    const { hasShutterStockAccess } = this.state;
    const totalCostStr = toBeautifulDecimal(+totalCost);
    const totalWithoutExtras = `${getCurrencySymbol()}${totalCostStr}`;

    if (!isEstimatedTotal) return totalWithoutExtras;
    if (loadingStockAssets) return '-';

    return appliedStockAssets?.length ?
    `${getCurrencySymbol()}${addExtrasTotalCost(totalCostStr, appliedStockAssets.map((t) => ({
      ...t,
      cost: hasShutterStockAccess ? '0' : t.cost
    })))}` :
    totalWithoutExtras;
  }

  // Read production credits from Finance — the source of truth once the
  // finance-ledger flag is on. The static per-output path (getOutputCreditAmount)
  // hardcodes every non-master output to 0, so a credit-funded Social Iteration
  // is invisible to it; the Costs tab reads Finance and counts it. This makes the
  // Overview tab + customer Completed/Review summary (both rendered through this
  // component) agree with the Costs tab. Mirrors useVideoCredits' per-output read.
  fetchFinanceCredits = async () => {
    const { flags, getFinanceBillables, videoOutputs, additionalOutputCost } =
    this.props;

    const financeLedgerEnabled = Boolean(flags && flags.financeLedgerEnabled);

    // The org's current billing-region default iteration cost (from the Addons
    // package constants, wired through the container). When available, unbilled
    // iterations resolve to this — matching the Costs tab — via
    // getEffectiveIterationCost. When it isn't (provider absent at a render
    // site), we fall back to legacy behaviour: only reprice billed outputs.
    const hasRegionDefault =
    additionalOutputCost !== undefined &&
    additionalOutputCost !== null &&
    additionalOutputCost !== '';
    const currentRegionDefaultCost = hasRegionDefault ?
    getCostValue({ cost: additionalOutputCost }) :
    undefined;

    // Off-flag, or without the Finance request wired in, leave the finance
    // totals null so the displayed total falls back to the legacy static sum.
    if (!financeLedgerEnabled || !getFinanceBillables ||
    !videoOutputs || videoOutputs.length === 0) {
      return;
    }

    const masterOutput = videoOutputs.find((output) => Boolean(output.isMaster));
    const masterOutputId = masterOutput && masterOutput.outputId;

    try {
      // Each output is its own productRecordId. Production credits are summed
      // across every output; creative-services credits live on the master output
      // only (mirrors useVideoCredits' read so the totals match the Costs tab).
      const perOutput = await Promise.all(
      videoOutputs.map(async (output) => {
        try {
          const { data } = await getFinanceBillables({
            productRecordId: output.outputId
          });
          const records = _get(data, 'getFinanceBillables.records', []) || [];
          const derived = deriveFinanceCredits(records);
          return {
            outputId: output.outputId,
            prodCredits: derived.prodCredits,
            creativeServiceCredits: derived.creativeServiceCredits,
            // Dollar iteration cost from Finance; only meaningful when the output
            // actually has a record (hasFinanceRecord), otherwise the summary
            // keeps its legacy `outputCost` snapshot for that output.
            iterationCost: derived.iterationCost,
            hasFinanceRecord: records.length > 0,
            ok: true
          };
        } catch (err) {
          // A single output's read error contributes 0 (a missing record means
          // the ledger holds no charge for that output) — see the all-failed
          // guard below for the total-failure case.
          return {
            outputId: output.outputId,
            prodCredits: 0,
            creativeServiceCredits: 0,
            iterationCost: 0,
            hasFinanceRecord: false,
            ok: false
          };
        }
      }));

      // If EVERY read failed — e.g. an unauthenticated public review link where
      // Finance isn't reachable, or a Finance outage — treat Finance as
      // unavailable and keep the totals null so the summary falls back to the
      // legacy static total rather than rendering a false 0. A legitimately
      // empty ledger still reads as 0 here (reads succeed) and is shown as such,
      // matching the Costs tab.
      if (!perOutput.some(({ ok }) => ok)) {
        return;
      }

      const financeProdCredits = perOutput.reduce(
      (total, { prodCredits }) => total + Number(prodCredits || 0), 0);

      const masterEntry = perOutput.find(
      ({ outputId }) => outputId === masterOutputId);
      const financeCreativeServiceCredits = masterEntry ?
      Number(masterEntry.creativeServiceCredits || 0) :
      0;

      // Keep the per-output breakdown so the V2 dropdown can show each output's
      // real production ("Credits Used") and creative-services credits, instead
      // of the legacy contract/getCreditsConsumption values (0 for ledger-only
      // charges) that left the breakdown reading "0 Credit".
      const financePerOutputCredits = perOutput.reduce(
      (map, { outputId, prodCredits, creativeServiceCredits }) => {
        map[outputId] = {
          prodCredits: Number(prodCredits || 0),
          creativeServiceCredits: Number(creativeServiceCredits || 0)
        };
        return map;
      }, {});

      // Iteration ($) cost per output, resolved through the SHARED policy
      // (getEffectiveIterationCost) so this summary and the Costs tab can't
      // drift: billed → Finance $, unbilled → current region default (never the
      // stale snapshot). Without a region default in scope, keep legacy
      // behaviour — only billed outputs get an override, unbilled keep their
      // snapshot.
      const financePerOutputIterationCost = perOutput.reduce(
      (map, { outputId, iterationCost, hasFinanceRecord }) => {
        if (hasRegionDefault) {
          map[outputId] = getEffectiveIterationCost({
            financeLedgerEnabled,
            hasFinanceRecord,
            financeCost: hasFinanceRecord ? Number(iterationCost || 0) : undefined,
            currentRegionDefaultCost
          });
        } else if (hasFinanceRecord) {
          map[outputId] = Number(iterationCost || 0);
        }
        return map;
      }, {});

      this.setState(
        {
          financeProdCredits,
          financeCreativeServiceCredits,
          financePerOutputCredits,
          financePerOutputIterationCost
        },
        // Recompute the dollar total now that Finance has resolved — setTotalCost
        // runs on mount before this read returns, so the initial total is based
        // on the stale `outputCost` snapshots until we reprice here.
        () => this.setTotalCost()
      );
    } catch (err) {
      // Leave the finance totals unchanged (null on first load) so the summary
      // falls back to the static total rather than rendering a wrong number.
    }
  };

  getTotalContractVideoCredits() {
    const { contractOutputCredits, financeProdCredits } = this.state;

    const staticProdCredits = contractOutputCredits ?
    Object.values(contractOutputCredits)
    // $FlowFixMe
    .reduce((total, creditAmount) => total + creditAmount, 0) :
    0;

    return resolveDisplayedProdCredits({
      financeProdCredits,
      staticProdCredits
    });
  }

  calculateTotalProjectCost = () => {
    const {
      totalCost,
      totalProjectCost: prevStateCost,
      creativeServicesCredits,
      financeProdCredits,
      financeCreativeServiceCredits
    } = this.state;
    const { getTotalProjectCost } = this.props;

    const financeLoaded =
    financeProdCredits !== null && financeProdCredits !== undefined;

    let totalCreditsForProjectExcludingAddons;
    if (financeLoaded) {
      // Finance is the source of truth: the project credit total is its
      // production credits (master + credit-funded iterations) plus creative
      // services, matching the Costs tab regardless of subscription model.
      const financeTotalCredits =
      financeProdCredits + (financeCreativeServiceCredits || 0);
      totalCreditsForProjectExcludingAddons = formatCreditsLabel(financeTotalCredits);
    } else {
      const totalCreditsFromContracts =
      this.getTotalContractVideoCredits() + creativeServicesCredits;
      totalCreditsForProjectExcludingAddons = formatCreditsLabel(totalCreditsFromContracts);
    }

    const totalProjectCost = `${totalCreditsForProjectExcludingAddons} + ${this.getFormattedTotalCost(totalCost, true)}`;

    if (totalProjectCost !== prevStateCost) {
      this.setState({
        totalProjectCost,
        totalProjectCredits: totalCreditsForProjectExcludingAddons
      });
    }

    if (!getTotalProjectCost) {
      return;
    }

    getTotalProjectCost(totalProjectCost);
  };

  paidStatusSuffix = () =>
  <div className="payment-summary-content__payment-status">
      <span className="payment-summary-content__payment-status--completed">Paid</span>
    </div>;

  dollarsPaidStatusSuffix = () =>
  <div className="payment-summary-content__payment-status">
      <span className="payment-summary-content__payment-status--completed">Shootsta Dollars Paid</span>
    </div>;

  // Dollars charged but not yet settled (withheld / approval requested) — the
  // amount is reserved against the workspace balance, awaiting finance approval.
  dollarsPendingStatusSuffix = () =>
  <div className="payment-summary-content__payment-status">
      <span className="payment-summary-content__payment-status--pending">Shootsta Dollars Pending</span>
    </div>;


  onClickReportBillingConcern = () => {
    const { videoId, videoReadableId } = this.props;

    const {
      user: { email, firstName, lastName }
    } = credentials.get();

    const SUPPORT_EMAIL = 'support@shootsta.com';

    const subject = encodeURIComponent('Billing concern');
    const projectLink = `${window.location.origin}/pro/project/${videoId}`;

    const body = encodeURIComponent(
      `Name: ${firstName} ${lastName}
Email: ${email}
Project ID: ${videoReadableId}
Project Link: ${projectLink}`
    );

    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  renderReportBillingConcern = () =>
  <Link
    className="payment-summary-content__credits-total__summary-header--link"
    onClick={this.onClickReportBillingConcern}>

      Report a Billing Concern
    </Link>;


  setContractConsumptionCredits = async () => {
    const {
      videoOutputs,
      getCreditsConsumption,
      userSubmittedTeamId,
      videoId,
      resourceType = 'PRO'
    } = this.props;

    const owner = userSubmittedTeamId ? `TEAM|${userSubmittedTeamId}` : 'WORKSPACE';

    const teamContractOutputCreditsMap = userSubmittedTeamId ?
    await Promise.all(
      videoOutputs.map(async (output) => {
        const { data, error } = await getCreditsConsumption({
          owner: `TEAM|${userSubmittedTeamId}`,
          resourceType,
          resourceToSearch: `${videoId}|${output.outputId}`
        });

        if (!data || error) {
          return {
            outputId: output.outputId,
            credits: 0
          };
        }

        const { consumedCredits } = _get(data, 'getCreditsConsumption', {});

        return {
          outputId: output.outputId,
          credits: Number(consumedCredits || 0)
        };
      })
    ) :
    [];

    const workspaceContractOutputCreditsMap = await Promise.all(videoOutputs.map(async (output) => {
      const { data, error } = await getCreditsConsumption({
        owner: 'WORKSPACE',
        resourceType,
        resourceToSearch: `${videoId}|${output.outputId}`
      });

      if (!data || error) {
        return {
          outputId: output.outputId,
          credits: 0
        };
      }

      const { consumedCredits } = _get(data, 'getCreditsConsumption', {});

      return {
        outputId: output.outputId,
        credits: Number(consumedCredits || 0)
      };
    }));

    const contractOutputCredits = [
    ...teamContractOutputCreditsMap,
    ...workspaceContractOutputCreditsMap].
    reduce((acc, { outputId, credits }) => {
      if (!acc[outputId]) {
        acc[outputId] = 0;
      }
      acc[outputId] += Number(credits);
      return acc;
    }, {});


    const { data, error } = await getCreditsConsumption({
      owner,
      resourceType: 'CREATIVE_SERVICES',
      resourceToSearch: videoId
    });
    const hasError = !data || error;
    const creativeServicesCredits = hasError ? 0 : _get(data, 'getCreditsConsumption.consumedCredits', 0);

    this.setState({
      loading: false,
      contractOutputCredits,
      initialContractOutputCredits: contractOutputCredits,
      creativeServicesCredits: Number(creativeServicesCredits || 0),
      initialCreativeServicesCredits: Number(creativeServicesCredits || 0)
    });
  };

  fetchCreditsDetails = async () => {
    const {
      getOrganisation,
      getTeam,
      userSubmittedTeamId,
      getContracts,
      organisationId
    } = this.props;

    const [orgResponse, contractsResponse, teamResponse] = await Promise.all([
    getOrganisation(),
    getContracts({
      organisationId,
      product: 'WORKSPACE',
      status: 'ACTIVE'
    }),
    ...(userSubmittedTeamId ? [getTeam(userSubmittedTeamId)] : [])]
    );

    const team = _get(teamResponse, 'data.getTeam');

    const availableCredits = userSubmittedTeamId && team ?
    _get(team, 'creditBalance.balance', 0) :
    _get(orgResponse, 'data.getOrganisation.creditBalance.balance', 0);

    const contractsLength = _get(contractsResponse, 'data.getContracts', []).length;

    this.setContractConsumptionCredits();

    this.setState({
      hasShutterStockAccess: _get(orgResponse, 'data.getOrganisation.shutterstockAccess', false)
    });

    this.setState({
      loading: false,
      availableCredits,
      hasActiveContract: Boolean(contractsLength)
    });
  };

  setTotalCost = () => {
    const outputsValues = this.getOutputsValues();

    const totalCost = this.calculateTotalCost(outputsValues);

    const beautifiedTotalCost = toBeautifulDecimal(totalCost);

    this.setState(() => ({
      totalCost: beautifiedTotalCost
    }));
  };

  calculateTotalCost = (outputValues) => outputValues.
  reduce((acc, outputValue) => acc + +outputValue.totalCost, 0);

  updatePurchaseOrderId = async (toUpdate) => {
    const { videoOutputs, updatePurchaseOrderId, onPurchaseOrderIdChange } = this.props;

    const versionId = getVersionIdWithPurchaseOrderId(videoOutputs);

    const { error, data } = await updatePurchaseOrderId({
      versionId,
      purchaseOrderId: toUpdate
    });

    if (error || !data) {return;}

    if (onPurchaseOrderIdChange) {
      onPurchaseOrderIdChange(data.updatePurchaseOrderId);
    }
  };

  handlePurchaseOrderIdChange = async (value) => {
    this.setState({ purchaseOrderId: value });
    this.updatePurchaseOrderId(value);
  };

  handleContractPurchaseIdChange = (e) => {
    this.setState({ purchaseOrderId: e.target.value });
  };

  onPurchaseIdUpdate = async () => {
    const { purchaseOrderId } = this.state;
    this.updatePurchaseOrderId(purchaseOrderId);
  };

  loadPaymentStatus = async () => {
    const {
      getVideoPaymentStatus,
      videoOutputs,
      videoId,
      flags
    } = this.props;

    const { enabledPaymentGateway = false } = flags || {};

    if (!enabledPaymentGateway || !getVideoPaymentStatus) return;

    if (_isEmpty(videoOutputs)) return;

    const { data: paymentStatusData, error: paymentStatusError } = await getVideoPaymentStatus({
      videoId
    });

    if (paymentStatusError || !paymentStatusData) return;

    const { getVideoPaymentStatus: { status, totalAmountPending = 0 } } = paymentStatusData;

    this.setState({
      paymentStatus: status,
      totalAmountPending
    });
  };

  // Finance is the source of truth for "is this paid". Stripe's status only
  // flips to SUCCEEDED for gateway card payments — Dollars, manual
  // mark-as-paid and legacy/invoiced settlements all leave it OPEN. We read
  // the finance record status instead so every surface that renders this
  // modal (project list, video review, traffic, completed video) stops
  // offering a duplicate "Pay now" once a project is settled.
  loadAddonsSettlement = async () => {
    const {
      getFinanceBillables,
      videoOutputs,
      videoReadableId,
      flags
    } = this.props;

    const { financeLedgerEnabled = false } = flags || {};

    // Mark settlement "resolved" on every exit so the pay button (which is
    // held until we know the settled state, to avoid flashing "Pay Now" then
    // flipping to "Paid") always eventually renders.
    if (!financeLedgerEnabled || !getFinanceBillables) {
      this.setState({ settlementLoaded: true });
      return;
    }

    // Native projects are keyed per output; legacy/migrated projects are keyed
    // by the project readableId. The finance proxy resolves an outputId UUID to
    // its readableId and passes a non-UUID readableId straight through, so we
    // query both and merge.
    const productRecordIds = [
    ...(videoOutputs || []).
    map((output) => output && output.outputId).
    filter(Boolean),
    videoReadableId].
    filter(Boolean);

    if (productRecordIds.length === 0) {
      this.setState({ settlementLoaded: true });
      return;
    }

    const recordSets = await Promise.all(
      productRecordIds.map(async (productRecordId) => {
        try {
          const { data } = await getFinanceBillables({ productRecordId });
          return _get(data, 'getFinanceBillables.records', []) || [];
        } catch (e) {
          return [];
        }
      })
    );

    const byRecordId = new Map();
    recordSets.flat().forEach((record) => {
      if (record && record.recordId) byRecordId.set(record.recordId, record);
    });
    const billables = Array.from(byRecordId.values());

    const settled = isProjectSettled(billables);
    this.setState({
      addonsSettled: settled,
      settledWithDollars: settled && hasDollarBillables(billables),
      usedDollars: hasDollarBillables(billables),
      settlementLoaded: true
    });
  };

  handleOnClickPay = async () => {
    const { customPaymentCheckout, videoId, videoOutputs } = this.props;

    const masterOutputId = videoOutputs.find(({ isMaster }) => isMaster)?.outputId;
    if (!masterOutputId) {
      console.error('No master output ID found');
      return;
    }

    const originUrl = window.location.href;

    if (!customPaymentCheckout) return;

    const { data, error } = await customPaymentCheckout({
      videoId,
      outputId: masterOutputId,
      originUrl,
      paymentType: 'PROJECT_COMPLETED'
    });

    if (!data || error) return;

    const { customPaymentCheckout: checkoutData } = data || {};

    const { checkoutUrl } = checkoutData;
    if (_isEmpty(checkoutUrl)) return;

    window.location.href = checkoutUrl;
  };

  handleOpenInvoices = () => {
    const {
      videoId,
      organisationId,
      videoReadableId,
      videoName,
      videoOutputs,
      handlePrint
    } = this.props;

    const masterOutput = videoOutputs.find(({ isMaster }) => isMaster);

    const { completedAt } = masterOutput || {};

    const videoDetails = {
      videoId,
      organisationId,
      isUrl: true,
      videoReadableId,
      projectName: videoName,
      completedAt
    };

    handlePrint(videoDetails);
  };

  render() {
    const {
      flags,
      visible,
      onClose,
      onConfirm,
      showPrintBtn,
      printText,
      handlePrint,
      initialOpenIndex,
      componentRef,
      appliedStockAssets,
      ghost,
      hideCreditsModification,
      videoReadableId,
      videoId,
      videoOutputs,
      isShutterStockUnlimited = false,
      getCurrencyIcon
    } = this.props;

    const {
      loading,
      totalCost,
      purchaseOrderId,
      contractOutputCredits,
      creativeServicesCredits,
      financeCreativeServiceCredits,
      financePerOutputCredits,
      hasShutterStockAccess,
      totalProjectCost,
      paymentStatus,
      totalAmountPending,
      addonsSettled,
      settledWithDollars,
      usedDollars,
      settlementLoaded
    } = this.state;

    const { enabledPaymentGateway = false, financeLedgerEnabled = false } =
    flags || {};

    // Display value for the "Creative Services" line item. When Finance is the
    // source of truth it must match the Subtotal, which sums
    // financeProdCredits + financeCreativeServiceCredits (calculateTotalProjectCost);
    // otherwise it falls back to the legacy getCreditsConsumption value so the
    // off-flag path and the public review-link fallback are unchanged.
    const displayedCreativeServicesCredits = resolveDisplayedCreativeServiceCredits({
      financeCreativeServiceCredits,
      staticCreativeServiceCredits: creativeServicesCredits
    });

    if (totalCost === -1) {return null;}

    const shouldShowPrintBtn = ghost ? false : showPrintBtn;

    if (loading) {
      return (
        <FormModal visible={visible} hideFooter showHeaderCloseButton={false}>
          <Loader
            hasNoBeforeShowTimeout
            text="Fetching Credit Details"
            caption="Please wait while we fetch credit details for you" />

        </FormModal>);

    }

    const isEachOutputsApproved = videoOutputs &&
    videoOutputs.every(({ status }) => status?.name === VIDEO_OUTPUT_STATUS.APPROVED);

    // Stripe only flips paymentStatus to SUCCEEDED for gateway card payments.
    // Everything else Finance can settle — Dollars deducted from the wallet,
    // accounts marking an invoice paid, and legacy/migrated projects — leaves
    // the Stripe status OPEN. The caller (which owns the finance-ledger
    // billables) tells us when the add-ons are settled so we show the paid
    // state instead of offering a second, duplicate card payment.
    const settledViaLedger = enabledPaymentGateway && addonsSettled;

    // Dollars charged but not yet settled (withheld / approval requested) are
    // already reserved against the workspace balance, awaiting finance approval —
    // never offer a second, duplicate card payment on top. Mirrors the
    // AddonsPayment widget's `usedDollars` guard.
    const dollarsReserved = enabledPaymentGateway && usedDollars && !addonsSettled;

    // Hold the pay button until the finance settlement check has resolved, so
    // a settled project never flashes "Pay now" before flipping to "Paid".
    const settlementResolved = !financeLedgerEnabled || settlementLoaded;

    const canProcessPayment = enabledPaymentGateway &&
    [PAYMENT_STATUS.OPEN, PAYMENT_STATUS.REFUNDED].includes(paymentStatus) &&
    totalAmountPending !== 0 &&
    isEachOutputsApproved &&
    !settledViaLedger &&
    !dollarsReserved &&
    settlementResolved;

    const paidByCard = enabledPaymentGateway && paymentStatus === PAYMENT_STATUS.SUCCEEDED;

    // Gateway card payment takes precedence: it keeps its Stripe invoice.
    const settledByLedgerOnly = settledViaLedger && !paidByCard;

    // hasPaid drives the "settled" layout (paid breakdown, no pay prompt) in
    // both content variants — true for either settlement route.
    const hasPaid = paidByCard || settledByLedgerOnly;

    // Stripe's pending total only zeroes for card payments, so ledger-settled
    // projects (e.g. Shootsta Dollars) keep a non-zero value despite owing 0.
    const displayedAmountPending =
    financeLedgerEnabled && settledByLedgerOnly ? 0 : totalAmountPending;

    const paymentGatewayProps = canProcessPayment ?
    {
      saveText:
      <AmountDue
        totalAmountDue={totalAmountPending}
        getCurrencyIcon={getCurrencyIcon} />,


      onSave: this.handleOnClickPay
    } :
    {};

    return (
      <div className={cx('payment-summary', 'payment-summary--new-subscription-model')}>
        <FormModal
          visible={visible}
          onConfirm={onConfirm}
          onClose={onClose}
          showPrintBtn={shouldShowPrintBtn || paidByCard}
          handlePrint={paidByCard ? this.handleOpenInvoices : handlePrint}
          componentRef={componentRef}
          printText={paidByCard ? 'View Invoice' : printText}
          printBtnType={paidByCard ? 'SECONDARY' : 'PRIMARY'}
          closeText="Close"
          title="Project Cost Summary"
          stayOpenOnSave={false}
          footerChildren={hasPaid ? this.renderReportBillingConcern() : null}
          headerSuffix={
          settledByLedgerOnly ?
          settledWithDollars ?
          this.dollarsPaidStatusSuffix() :
          this.paidStatusSuffix() :
          paidByCard ?
          this.paidStatusSuffix() :
          dollarsReserved ?
          this.dollarsPendingStatusSuffix() :
          null}
          {...paymentGatewayProps}>

          <PaymentSummaryContentV2
            videoId={videoId}
            purchaseOrderId={purchaseOrderId}
            ghost={ghost}
            initialOpenIndex={initialOpenIndex}
            hideCreditsModification={hideCreditsModification}
            totalCost={totalCost}
            appliedStockAssets={appliedStockAssets}
            outputCreditAmounts={contractOutputCredits}
            videoReadableId={videoReadableId}
            summaryCreativeServicesCredits={displayedCreativeServicesCredits}
            financePerOutputCredits={financePerOutputCredits}
            getVideoCreditsCostValue={this.getTotalContractVideoCredits()}
            handlePurchaseOrderIdChange={this.handleContractPurchaseIdChange}
            outputValues={this.getOutputsValues()}
            getFormattedTotalCost={
            (outputTotalCost, isEstimatedTotal = false) => this.getFormattedTotalCost(
              outputTotalCost, isEstimatedTotal
            )}

            onPurchaseIdUpdate={this.onPurchaseIdUpdate}
            hasShutterStockAccess={hasShutterStockAccess && isShutterStockUnlimited}
            totalProjectCost={totalProjectCost}
            handlePrint={handlePrint}
            canProcessPayment={canProcessPayment}
            hasPaid={hasPaid}
            amountPending={displayedAmountPending} />

        </FormModal>
      </div>);

  }
}

export default withStockAssets(PaymentSummaryComponent);